import { Button, Input, InputNumber, Modal, Radio, Select, Switch, Tag, Tour, message } from 'antd';
import type { TourProps } from 'antd';
import i18next from 'i18next';
import { isLoggedIn, listAccountFonts, saveAccountFont, fetchAccountFontBytes } from './library';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import Marmotte from './Marmotte';
import { TutoLink } from './Tutos';
import { DTFItem } from './packing';
import { DTFSettings } from './settings';
import {
	BuiltText,
	buildTextSvg,
	findUnsupportedChars,
	listCustomFonts,
	loadCustomFonts,
	registerCustomFont,
	registerCustomFontBytes,
	customFontBytes,
	customIdOfSha,
	listValues,
	rangeValues,
	SERIES_MAX,
	TEXT_COLORS,
	TEXT_FONTS,
	TextStyle,
} from './textItem';

export interface TextEntry {
	value: string;
	style: TextStyle;
	built: BuiltText;
}

interface IProps {
	open: boolean;
	/** Item texte en cours de modification (null = création) */
	editItem: DTFItem | null;
	/** Damier sombre dans l'aperçu (suivi du fond de planche) */
	dark?: boolean;
	settings: DTFSettings;
	/** heightsCm : hauteur principale + tailles additionnelles — la même liste est générée dans chaque taille */
	onCreate: (entries: TextEntry[], heightsCm: number[]) => void;
	onSaveEdit: (id: string, entry: TextEntry) => void;
	onCancel: () => void;
}

type Mode = 'un' | 'serie' | 'liste';

type FontGroup = { label: string; options: Array<{ value: string; label: string }> };
const buildFontOptions = (extra: Array<{ id: string; label: string; group: string }>): FontGroup[] =>
	([...extra, ...TEXT_FONTS] as Array<{ id: string; label: string; group: string }>).reduce<FontGroup[]>((groups, f) => {
		let g = groups.find(x => x.label === f.group);
		if (!g) {
			g = { label: f.group, options: [] };
			groups.push(g);
		}
		g.options.push({ value: f.id, label: f.label });
		return groups;
	}, []);

export const Palette: React.FC<{ value: string; onChange: (c: string) => void }> = ({ value, onChange }) => (
	<div className="dtf-txt-palette">
		{TEXT_COLORS.map(c => (
			<button
				key={c}
				type="button"
				className={`dtf-txt-swatch${value.toUpperCase() === c.toUpperCase() ? ' selected' : ''}`}
				style={{ background: c }}
				onClick={() => onChange(c)}
				aria-label={c}
			/>
		))}
		{/* 26.10 (Daniel) : n'importe quelle couleur, pas seulement la palette — le sélecteur natif était un carré noir que personne ne voyait */}
		<label
			className={`dtf-txt-custom-wrap${value && !TEXT_COLORS.some(c => c.toUpperCase() === value.toUpperCase()) ? ' selected' : ''}`}
			title={i18next.t('dtf.txt-couleur-perso') as string}
		>
			<span
				className="dtf-txt-custom-dot"
				style={value && !TEXT_COLORS.some(c => c.toUpperCase() === value.toUpperCase()) ? { background: value } : undefined}
				aria-hidden="true"
			/>
			<span>{i18next.t('dtf.txt-couleur-autre')}</span>
			<input
				type="color"
				className="dtf-txt-custom"
				value={value || '#111111'}
				onChange={e => onChange(e.target.value)}
				aria-label={i18next.t('dtf.txt-couleur-perso') as string}
			/>
		</label>
	</div>
);

const TextModal: React.FC<IProps> = ({ open, editItem, dark, settings, onCreate, onSaveEdit, onCancel }) => {
	const [mode, setMode] = useState<Mode>('serie');
	const [value, setValue] = useState('10');
	const [from, setFrom] = useState(1);
	const [to, setTo] = useState(20);
	const [listVals, setListVals] = useState<string[]>([]);
	const [listInput, setListInput] = useState('');
	const [fontId, setFontId] = useState(TEXT_FONTS[0].id);
	// Polices importées par le client (Daniel 21.10.2026) : liste rechargée à l'ouverture, bouton d'import à côté du choix
	const [customFonts, setCustomFonts] = useState(listCustomFonts());
	const [fontBusy, setFontBusy] = useState(false);
	const fontOptions = useMemo(() => buildFontOptions(customFonts), [customFonts]);
	const fontInputRef = useRef<HTMLInputElement>(null);
	const onImportFont = async (file: File | null) => {
		if (!file) return;
		setFontBusy(true);
		try {
			const def = await registerCustomFont(file);
			setCustomFonts(listCustomFonts());
			setFontId(def.id);
			// connecté → la police part aussi avec le compte (retrouvée sur un autre appareil) ; sinon elle reste sur cet appareil
			let where = 'appareil';
			if (isLoggedIn()) {
				try {
					const bytes = customFontBytes(def.id);
					if (bytes) { await saveAccountFont(def.label, bytes); where = 'compte'; }
				} catch { where = 'appareil-erreur'; }
			}
			message.success(i18next.t(where === 'compte' ? 'dtf.txt-police-ok-compte' : 'dtf.txt-police-ok-appareil', { nom: def.label }) as string, 6);
		} catch (e) {
			const code = (e as Error).message;
			message.error(i18next.t(code === 'trop-gros' ? 'dtf.txt-police-trop-gros' : code === 'incomplete' ? 'dtf.txt-police-incomplete' : 'dtf.txt-police-illisible') as string, 6);
		} finally {
			setFontBusy(false);
			if (fontInputRef.current) fontInputRef.current.value = '';
		}
	};
	const [fill, setFill] = useState('#111111');
	const [outline, setOutline] = useState(false);
	const [strokeColor, setStrokeColor] = useState('#FFFFFF');
	const [strokePct, setStrokePct] = useState(6);
	const [heightCm, setHeightCm] = useState(10);
	// Demande Daniel (#220) : « ajouter une dimension pour ces numéros-là » — ex. 25 cm dos + petit numéro avant
	const [extraHeights, setExtraHeights] = useState<number[]>([]);
	const [previews, setPreviews] = useState<string[]>([]);
	const [busy, setBusy] = useState(false);

	// Explications guidées : automatiques à la toute première création, ❓ pour revoir
	const [tourOpen, setTourOpen] = useState(false);
	const closeTour = () => {
		localStorage.setItem('dtf_txt_tour_done', '1');
		setTourOpen(false);
	};

	useEffect(() => {
		if (!open) return;
		loadCustomFonts().then(async () => {
			setCustomFonts(listCustomFonts());
			// polices du compte absentes de cet appareil → rapatriées (silencieux, hors ligne = rien)
			if (!isLoggedIn()) return;
			try {
				const have = new Set(listCustomFonts().map(f => f.id));
				const remote = await listAccountFonts();
				let added = 0;
				for (const f of remote) {
					if (have.has(customIdOfSha(f.sha256))) continue;
					try { await registerCustomFontBytes(await fetchAccountFontBytes(f.id), f.display_name); added++; } catch { /* police illisible ou réseau : on passe */ }
				}
				if (added) setCustomFonts(listCustomFonts());
			} catch { /* bibliothèque indisponible : les polices de l'appareil suffisent */ }
		});
		if (editItem && editItem.text) {
			setTourOpen(false);
			setMode('un');
			setValue(editItem.text.value);
			setFontId(editItem.text.fontId);
			setFill(editItem.text.fill);
			setOutline(!!editItem.text.stroke);
			setStrokeColor(editItem.text.stroke || '#FFFFFF');
			setStrokePct(editItem.text.strokePct || 6);
			setHeightCm(editItem.heightCm);
		} else {
			// nouvelle création : on repart d'une liste vide (sinon les chips de la
			// série précédente restent et se recréent en doublon), mais on garde
			// police/couleur/hauteur — choix de style que l'utilisateur veut répéter
			setListVals([]);
			setListInput('');
			setExtraHeights([]);
			setTourOpen(localStorage.getItem('dtf_txt_tour_done') !== '1');
		}
	}, [open, editItem]);

	const addListVals = (raw: string) => {
		const vals = listValues(raw);
		if (!vals.length) return;
		setListVals(prev => [...prev, ...vals].slice(0, SERIES_MAX));
		setListInput('');
	};

	const pending = listInput.trim();
	const values =
		mode === 'un'
			? value.trim()
				? [value.trim()]
				: []
			: mode === 'serie'
				? rangeValues(from, to)
				: [...listVals, ...(pending ? [pending] : [])].slice(0, SERIES_MAX);

	const style = (v: string): TextStyle => ({
		value: v,
		fontId,
		fill,
		stroke: outline ? strokeColor : '',
		strokePct,
	});

	// Aperçu de TOUTES les valeurs (débouncé : série de 100 = 100 petits SVG à générer)
	const previewVals = values.length ? values : ['10'];
	const valuesKey = previewVals.join('\u0001');
	useEffect(() => {
		if (!open) return undefined;
		let alive = true;
		const t = setTimeout(async () => {
			try {
				const vals = valuesKey.split('\u0001');
				const out: string[] = [];
				for (const v of vals) {
					const b = await buildTextSvg(style(v));
					if (!alive) return;
					out.push(b.src);
				}
				if (alive) setPreviews(out);
			} catch (_e) {
				if (alive) setPreviews([]);
			}
		}, 250);
		return () => {
			alive = false;
			clearTimeout(t);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, valuesKey, fontId, fill, outline, strokeColor, strokePct]);

	const usableH = settings.maxPlancheCm;
	const nTotal = values.length * (1 + extraHeights.length);

	const onOk = async () => {
		if (!values.length) {
			message.warning(i18next.t('dtf.txt-vide') as string);
			return;
		}
		setBusy(true);
		try {
			const bad = await findUnsupportedChars(fontId, values.join(''));
			if (bad) {
				message.error(i18next.t('dtf.txt-glyphes', { chars: bad }) as string, 6);
				return;
			}
			if (editItem) {
				const st = style(values[0]);
				const built = await buildTextSvg(st);
				onSaveEdit(editItem.id, { value: values[0], style: st, built });
			} else {
				const entries: TextEntry[] = [];
				for (const v of values) {
					const st = style(v);
					entries.push({ value: v, style: st, built: await buildTextSvg(st) });
				}
				onCreate(entries, [heightCm, ...extraHeights]);
			}
		} catch (_e) {
			message.error(i18next.t('dtf.txt-err') as string);
		} finally {
			setBusy(false);
		}
	};

	const tourSteps: TourProps['steps'] = [
		{
			title: i18next.t('dtf.txt-tour-modes-t') as string,
			description: i18next.t('dtf.txt-tour-modes-d') as string,
			target: () => document.querySelector('.dtf-txt-modes') as HTMLElement,
		},
		{
			title: i18next.t('dtf.txt-tour-valeurs-t') as string,
			description: i18next.t('dtf.txt-tour-valeurs-d') as string,
			target: () => document.querySelector('.dtf-txt-values') as HTMLElement,
		},
		{
			title: i18next.t('dtf.txt-tour-style-t') as string,
			description: i18next.t('dtf.txt-tour-style-d') as string,
			target: () => document.querySelector('.dtf-txt-style') as HTMLElement,
		},
		{
			title: i18next.t('dtf.txt-tour-hauteur-t') as string,
			description: i18next.t('dtf.txt-tour-hauteur-d') as string,
			target: () => document.querySelector('.dtf-txt-height') as HTMLElement,
		},
		{
			title: i18next.t('dtf.txt-tour-apercu-t') as string,
			description: i18next.t('dtf.txt-tour-apercu-d') as string,
			target: () => document.querySelector('.dtf-txt-preview-wrap') as HTMLElement,
		},
		{
			title: i18next.t('dtf.txt-tour-creer-t') as string,
			description: i18next.t('dtf.txt-tour-creer-d') as string,
			target: () => document.querySelector('.dtf-txt-modal .ant-btn-primary') as HTMLElement,
		},
	];

	return (
		<Modal centered
			open={open}
			className="dtf-txt-modal"
			title={
				editItem ? (
					i18next.t('dtf.txt-titre-edit')
				) : (
					<span>
						{i18next.t('dtf.txt-titre')}
						<Button
							size="small"
							type="text"
							className="dtf-modal-tour-btn"
							title={i18next.t('dtf.tour-revoir-mini') as string}
							onClick={() => setTourOpen(true)}
						>
							❓
						</Button>
					</span>
				)
			}
			okText={editItem ? i18next.t('dtf.enregistrer') : i18next.t('dtf.txt-creer', { n: nTotal })}
			cancelText={i18next.t('dtf.annuler')}
			onOk={onOk}
			onCancel={onCancel}
			okButtonProps={{ disabled: !values.length }}
			confirmLoading={busy}
			width={560}
			destroyOnClose
		>
			{!editItem && (
				<Marmotte pose="design" tone="info" size={52} title={i18next.t('dtf.mmt-texte-t') as string}>
					{i18next.t('dtf.mmt-texte')}
					<TutoLink n={8} />
				</Marmotte>
			)}
			{!editItem && (
				<Radio.Group
					className="dtf-txt-modes"
					value={mode}
					onChange={e => setMode(e.target.value)}
					optionType="button"
					buttonStyle="solid"
				>
					<Radio.Button value="serie">{i18next.t('dtf.txt-mode-serie')}</Radio.Button>
					<Radio.Button value="liste">{i18next.t('dtf.txt-mode-liste')}</Radio.Button>
					<Radio.Button value="un">{i18next.t('dtf.txt-mode-un')}</Radio.Button>
				</Radio.Group>
			)}

			<div className="dtf-txt-values">
			{mode === 'un' && (
				<label className="dtf-txt-field">
					{i18next.t('dtf.txt-valeur')}
					<Input
						value={value}
						maxLength={30}
						onChange={e => setValue(e.target.value)}
						placeholder={i18next.t('dtf.txt-valeur-ph') as string}
					/>
				</label>
			)}
			{mode === 'serie' && (
				<div className="dtf-txt-range">
					<label className="dtf-txt-field">
						{i18next.t('dtf.txt-de')}
						<InputNumber min={0} max={999} value={from} onChange={v => setFrom(Number(v) || 0)} />
					</label>
					<label className="dtf-txt-field">
						{i18next.t('dtf.txt-a')}
						<InputNumber min={0} max={999} value={to} onChange={v => setTo(Number(v) || 0)} />
					</label>
				</div>
			)}
			{mode === 'liste' && (
				<div className="dtf-txt-field">
					{i18next.t('dtf.txt-liste-aide')}
					{listVals.length > 0 && (
						<div className="dtf-txt-chips">
							{listVals.map((v, i) => (
								<Tag key={`${v}-${i}`} closable onClose={() => setListVals(prev => prev.filter((_x, j) => j !== i))}>
									{v}
								</Tag>
							))}
						</div>
					)}
					<div className="dtf-txt-addrow">
						<Input
							value={listInput}
							maxLength={200}
							placeholder={i18next.t('dtf.txt-valeur-ph') as string}
							onChange={e => setListInput(e.target.value)}
							onPressEnter={e => {
								e.preventDefault();
								addListVals(listInput);
							}}
							onPaste={e => {
								const txt = e.clipboardData.getData('text');
								if (/[\n;,\t]/.test(txt)) {
									e.preventDefault();
									addListVals(listInput + txt);
								}
							}}
						/>
						<Button onClick={() => addListVals(listInput)} disabled={!listInput.trim()} data-testid="txt-add">
							＋ {i18next.t('dtf.txt-ajouter')}
						</Button>
					</div>
				</div>
			)}
			</div>
			{!editItem && values.length >= SERIES_MAX && (
				<div className="dtf-txt-cap">⚠ {i18next.t('dtf.txt-plafond', { max: SERIES_MAX })}</div>
			)}

			<div className="dtf-txt-style">
			<label className="dtf-txt-field">
				{i18next.t('dtf.txt-police')}
				<Select
					value={fontId}
					onChange={setFontId}
					options={fontOptions}
					showSearch
					optionFilterProp="label"
					style={{ width: 260 }}
				/>
				<span className="dtf-txt-font-import">
					<Button size="small" loading={fontBusy} data-testid="font-import" onClick={() => fontInputRef.current && fontInputRef.current.click()} title={i18next.t('dtf.txt-police-importer-aide') as string}>
						{i18next.t('dtf.txt-police-importer')}
					</Button>
					<input ref={fontInputRef} type="file" accept=".ttf,.otf,.woff,font/ttf,font/otf,font/woff,application/font-sfnt,application/x-font-ttf" style={{ display: 'none' }} onChange={e => onImportFont(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
					<small>{i18next.t('dtf.txt-police-droits')}</small>
				</span>
			</label>

			<div className="dtf-txt-field">
				{i18next.t('dtf.txt-couleur')}
				<Palette value={fill} onChange={setFill} />
			</div>

			<div className="dtf-txt-field dtf-txt-outline-row">
				<span>
					{i18next.t('dtf.txt-contour')}{' '}
					<Switch size="small" checked={outline} onChange={setOutline} />
				</span>
				{outline && (
					<>
						<Palette value={strokeColor} onChange={setStrokeColor} />
						<label className="dtf-txt-inline">
							{i18next.t('dtf.txt-contour-epaisseur')}
							<InputNumber min={1} max={20} value={strokePct} onChange={v => setStrokePct(Number(v) || 1)} />
						</label>
					</>
				)}
			</div>
			</div>

			<div
				className="dtf-txt-field dtf-txt-height"
				title={editItem ? (i18next.t('dtf.txt-hauteur-fixe') as string) : undefined}
			>
				<label className="dtf-txt-height-row">
					{i18next.t('dtf.txt-hauteur')}
					<InputNumber
						min={1}
						max={Math.floor(usableH)}
						step={0.5}
						value={heightCm}
						onChange={v => setHeightCm(Number(v) || 1)}
						disabled={!!editItem}
					/>
				</label>
				{!editItem &&
					extraHeights.map((h, i) => (
						<label className="dtf-txt-height-row dtf-txt-height-extra" key={i}>
							{i18next.t('dtf.txt-hauteur')}
							<InputNumber
								min={1}
								max={Math.floor(usableH)}
								step={0.5}
								value={h}
								onChange={v =>
									setExtraHeights(prev => prev.map((x, j) => (j === i ? Number(v) || 1 : x)))
								}
							/>
							<Button
								size="small"
								type="text"
								danger
								className="dtf-txt-height-del"
								aria-label={i18next.t('dtf.txt-taille-suppr') as string}
								title={i18next.t('dtf.txt-taille-suppr') as string}
								onClick={() => setExtraHeights(prev => prev.filter((_x, j) => j !== i))}
							>
								✕
							</Button>
						</label>
					))}
				{!editItem && (
					<Button
						size="small"
						className="dtf-txt-height-add"
						data-testid="txt-add-size"
						title={i18next.t('dtf.txt-taille-plus-titre') as string}
						onClick={() =>
							setExtraHeights(prev => [...prev, Math.max(1, Math.min(Math.floor(usableH), Math.round(heightCm / 2)))])
						}
					>
						{i18next.t('dtf.txt-taille-plus')}
					</Button>
				)}
				{!editItem && extraHeights.length > 0 && (
					<div className="dtf-txt-height-help">{i18next.t('dtf.txt-taille-aide', { n: extraHeights.length + 1 })}</div>
				)}
			</div>

			<div className="dtf-txt-preview-wrap">
				<div className="dtf-txt-preview-label">{i18next.t('dtf.txt-apercu')}</div>
				<div
					className={`dtf-txt-preview${dark ? ' dtf-board-dark' : ''}${previews.length > 1 ? ' dtf-txt-preview-multi' : ''}`}
				>
					{previews.map((src, i) => (
						<img key={i} src={src} alt="" data-testid="txt-preview" />
					))}
				</div>
				{!editItem && nTotal > 1 && (
					<div className="dtf-txt-count">{i18next.t('dtf.txt-nb', { n: nTotal })}</div>
				)}
			</div>
			{!editItem && <Tour open={tourOpen} onClose={closeTour} steps={tourSteps} />}
		</Modal>
	);
};

export default TextModal;
